import React, { forwardRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { SingleSelectInlineEditViewIsEditable } from '@atlassian/jira-issue-field-single-select-inline-edit-full/src/ui/single-select/lite/index.tsx';
import type { SingleSelectCell$key } from '@atlassian/jira-relay/src/__generated__/SingleSelectCell.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import {
	useIsInlineEditingExtendedFieldSupportEnabled,
	useIsSingleLineRowHeightEnabled,
} from '../../../../controllers/features/selectors.tsx';
import { DUPLICATE_FIELD_EDIT_WIDTH } from '../../../constants.tsx';

export type Props = {
	fieldRef: SingleSelectCell$key;
	isFirstField?: boolean;
	hasDuplicates?: boolean;
};

export const SingleSelectCell = ({ fieldRef, isFirstField, hasDuplicates }: Props) => {
	const data = useFragment<SingleSelectCell$key>(
		graphql`
			fragment SingleSelectCell on JiraSingleSelectField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...lite_issueFieldSingleSelectInlineEditFull_SingleSelectInlineEditViewIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);

	const isInlineEditingExtendedFieldSupportEnabled =
		useIsInlineEditingExtendedFieldSupportEnabled();

	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable =
		'lazyIsEditableInIssueView' in data && isInlineEditingExtendedFieldSupportEnabled
			? data.lazyIsEditableInIssueView
			: false;
	useLazyLoadEditability(data.id, isEditable);

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	if (hasDuplicates && isSingleLineRowHeightEnabled) {
		return (
			<SingleSelectInlineEditViewIsEditable
				attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
				spacing="default"
				editViewPopup
				editViewPopupAlignBlock="start"
				editViewPopupWidth={DUPLICATE_FIELD_EDIT_WIDTH}
				// @ts-expect-error: to unblock React 18.2.0 -> 18.3.1 version bump
				editViewPopupTarget={isFirstField ? CustomTarget : undefined}
				fragmentRef={data}
				isEditable={isEditable ?? false}
				menuPosition={isFirstField ? 'fixed' : 'absolute'}
				readViewFitContainerHeight
				shouldShowPopover={isFirstField && isSingleLineRowHeightEnabled}
			/>
		);
	}

	return (
		<SingleSelectInlineEditViewIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={isEditable ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			shouldShowPopover={isSingleLineRowHeightEnabled}
		/>
	);
};

const CustomTarget = forwardRef<HTMLDivElement, {}>((_, ref) => {
	return (
		<Box
			xcss={fg('nin_issue_table_rtl_fix') ? customTargetStyles : customTargetStylesOld}
			ref={ref}
		/>
	);
});

const customTargetStylesOld = xcss({
	width: 'space.0',
	position: 'absolute',
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	top: 'space.0',
});

const customTargetStyles = xcss({
	width: 'space.0',
	position: 'absolute',
	insetBlockStart: 'space.0',
});
